import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiDatabase, FiPackage } from 'react-icons/fi';
import { networkManager, type InventoryData, type ShipInventoryItem } from '../../systems/NetworkManager';
import { getEquipmentById, RESOURCES } from '@astra/shared';
import {
  PALETTE,
  stationContainerStyle,
  StationHeader,
  stationHintStyle,
} from './stationTheme';

interface Props {
  stationId: string;
  onBack: () => void;
}

interface BankItem {
  itemType: 'resource' | 'equipment';
  itemId: string;
  quantity: number;
}

interface ShipItem {
  itemType: 'resource' | 'equipment';
  itemId: string;
  quantity: number;
}

function displayName(itemType: 'resource' | 'equipment', itemId: string): string {
  if (itemType === 'resource') {
    const r = (RESOURCES as any)[itemId];
    return r?.name ?? itemId;
  }
  const eq = getEquipmentById(itemId);
  return eq?.name ?? itemId;
}

export function PrivateStationBank({ stationId, onBack }: Props) {
  const [bank, setBank] = useState<BankItem[]>([]);
  const [shipItems, setShipItems] = useState<ShipItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refreshBank = useCallback(() => {
    const room: any = (networkManager as any).room;
    room?.send('getPrivateStationBank', { stationId });
  }, [stationId]);

  const refreshShip = useCallback(() => {
    networkManager.requestInventoryData('');
  }, []);

  useEffect(() => {
    const room: any = (networkManager as any).room;
    if (!room) return;

    room.onMessage('privateStationBank', (msg: any) => {
      if (msg.error) setError(msg.error);
      else { setError(null); setBank(msg.items || []); }
    });
    room.onMessage('privateBankTransferResult', (msg: any) => {
      setBusy(false);
      if (!msg.success) setError(msg.error || 'Transfer failed');
      else { setError(null); refreshBank(); refreshShip(); }
    });

    // Preserve any pre-existing inventory handler and chain back to it.
    const prevHandler = networkManager.onInventoryData;
    networkManager.onInventoryData = (data: InventoryData) => {
      const items: ShipItem[] = (data.shipInventory || []).map((it: ShipInventoryItem) => ({
        itemType: it.itemType,
        itemId: it.itemId,
        quantity: it.quantity,
      }));
      setShipItems(items);
      if (prevHandler) prevHandler(data);
    };

    refreshBank();
    refreshShip();
    const id = setInterval(() => { refreshBank(); refreshShip(); }, 4000);
    return () => {
      clearInterval(id);
      networkManager.onInventoryData = prevHandler;
    };
  }, [stationId, refreshBank, refreshShip]);

  const send = (key: 'shipToPrivateBank' | 'privateBankToShip', it: BankItem | ShipItem, qty: number) => {
    if (busy || qty <= 0) return;
    const room: any = (networkManager as any).room;
    setBusy(true);
    setError(null);
    room?.send(key, { stationId, itemType: it.itemType, itemId: it.itemId, quantity: qty });
  };

  const askQty = (max: number): number => {
    const raw = prompt(`How many? (1–${max}, blank = all)`, String(max));
    if (raw === null) return 0;
    if (raw.trim() === '') return max;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(max, n);
  };

  const sortedBank = useMemo(() => [...bank].sort(byTypeThenId), [bank]);
  const sortedShip = useMemo(() => [...shipItems].sort(byTypeThenId), [shipItems]);

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title="STATION BANK (LOCAL)" hint="[ESC] Return to Station" />

      <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        {/* Ship cargo column */}
        <Column title="Ship Cargo" icon={<FiPackage size={14} />}>
          {sortedShip.length === 0 && <Empty>No cargo aboard.</Empty>}
          {sortedShip.map((it) => (
            <Row key={`${it.itemType}:${it.itemId}`}>
              <Name>
                <span style={{ color: PALETTE.DIM, fontSize: 10, marginRight: 6 }}>{it.itemType.toUpperCase()}</span>
                {displayName(it.itemType, it.itemId)}
              </Name>
              <Qty>{it.quantity}</Qty>
              <button
                disabled={busy}
                onClick={() => {
                  const q = askQty(it.quantity);
                  if (q > 0) send('shipToPrivateBank', it, q);
                }}
                style={moveBtn}
                title="Move to station bank"
              >
                <FiArrowRight size={14} />
              </button>
            </Row>
          ))}
        </Column>

        {/* Station local bank column */}
        <Column title="Station Bank" icon={<FiDatabase size={14} />}>
          {sortedBank.length === 0 && <Empty>Bank is empty.</Empty>}
          {sortedBank.map((it) => (
            <Row key={`${it.itemType}:${it.itemId}`}>
              <button
                disabled={busy}
                onClick={() => {
                  const q = askQty(it.quantity);
                  if (q > 0) send('privateBankToShip', it, q);
                }}
                style={moveBtn}
                title="Move to ship cargo"
              >
                <FiArrowLeft size={14} />
              </button>
              <Qty>{it.quantity}</Qty>
              <Name style={{ textAlign: 'right' }}>
                {displayName(it.itemType, it.itemId)}
                <span style={{ color: PALETTE.DIM, fontSize: 10, marginLeft: 6 }}>{it.itemType.toUpperCase()}</span>
              </Name>
            </Row>
          ))}
        </Column>
      </div>

      {error && (
        <div style={{ padding: '8px 20px', color: PALETTE.RED, fontSize: 12, borderTop: `1px solid ${PALETTE.BORDER}` }}>
          {error}
        </div>
      )}
    </div>
  );
}

function byTypeThenId(a: BankItem, b: BankItem): number {
  if (a.itemType !== b.itemType) return a.itemType < b.itemType ? -1 : 1;
  return a.itemId < b.itemId ? -1 : 1;
}

function Column({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ backgroundColor: PALETTE.PANEL, border: `1px solid ${PALETTE.BORDER}`, borderRadius: 4, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 10, borderBottom: `1px solid ${PALETTE.BORDER}`, color: PALETTE.TEAL, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon}{title}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderBottom: `1px solid ${PALETTE.BORDER}`, fontSize: 13 }}>{children}</div>;
}
function Name({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <span style={{ flex: 1, color: PALETTE.LIGHT, ...style }}>{children}</span>;
}
function Qty({ children }: { children: React.ReactNode }) {
  return <span style={{ color: PALETTE.LIGHT, fontVariantNumeric: 'tabular-nums', minWidth: 50, textAlign: 'right' }}>{children}</span>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ color: PALETTE.DIM, fontSize: 12, padding: 8 }}>{children}</div>;
}

const moveBtn: React.CSSProperties = {
  padding: '4px 8px', border: `1px solid ${PALETTE.TEAL}`, background: 'transparent',
  color: PALETTE.TEAL, cursor: 'pointer', borderRadius: 3,
};

export default PrivateStationBank;
